import { z } from "zod";
import type { FieldType } from "@/types/forms";

const optionSchema = z.object({
  label: z.string().min(1),
  value: z.string().min(1),
});

const emojiOptionSchema = z.object({
  emoji: z.string().min(1),
  label: z.string().min(1),
  value: z.string().min(1),
});

// Common field editor schema (label + required)
export const fieldBaseSchema = z.object({
  label: z.string().min(1, "Label is required"),
  required: z.boolean(),
});

// Type-specific property schemas
export const propertySchemas: Record<FieldType, z.ZodSchema> = {
  short_text: z.object({
    placeholder: z.string().optional(),
    defaultValue: z.string().optional(),
  }),
  long_text: z.object({
    placeholder: z.string().optional(),
    defaultValue: z.string().optional(),
  }),
  rich_text: z.object({
    placeholder: z.string().optional(),
  }),
  number: z.object({
    placeholder: z.string().optional(),
    defaultValue: z.string().optional(),
  }),
  slider: z.object({
    min: z.coerce.number().default(0),
    max: z.coerce.number().default(100),
    step: z.coerce.number().default(1),
    defaultValue: z.string().optional(),
  }),
  rating: z.object({
    max: z.coerce.number().min(1).max(10).default(5),
    icon: z.enum(["star", "heart", "circle"]).default("star"),
  }),
  dropdown: z.object({
    options: z.array(optionSchema).min(1, "At least one option required"),
    placeholder: z.string().optional(),
  }),
  checkbox: z.object({
    options: z.array(optionSchema).min(1, "At least one option required"),
  }),
  yes_no: z.object({
    trueLabel: z.string().optional(),
    falseLabel: z.string().optional(),
  }),
  emoji_scale: z.object({
    emojis: z.array(emojiOptionSchema).min(1, "At least one emoji required"),
  }),
  date: z.object({
    placeholder: z.string().optional(),
  }),
  time: z.object({
    placeholder: z.string().optional(),
  }),
  datetime: z.object({
    placeholder: z.string().optional(),
  }),
};

// Validation schemas
export const validationSchemas: Record<FieldType, z.ZodSchema> = {
  short_text: z.object({
    minLength: z.coerce.number().optional(),
    maxLength: z.coerce.number().optional(),
    regex: z.string().optional(),
  }),
  long_text: z.object({
    minLength: z.coerce.number().optional(),
    maxLength: z.coerce.number().optional(),
  }),
  rich_text: z.object({
    minLength: z.coerce.number().optional(),
    maxLength: z.coerce.number().optional(),
  }),
  number: z.object({
    min: z.coerce.number().optional(),
    max: z.coerce.number().optional(),
  }),
  slider: z.object({}),
  rating: z.object({}),
  dropdown: z.object({}),
  checkbox: z.object({
    minSelect: z.coerce.number().optional(),
    maxSelect: z.coerce.number().optional(),
  }),
  yes_no: z.object({}),
  emoji_scale: z.object({}),
  date: z.object({}),
  time: z.object({}),
  datetime: z.object({}),
};

// Default properties per type
export const defaultProperties: Record<FieldType, unknown> = {
  short_text: { placeholder: "" },
  long_text: { placeholder: "" },
  rich_text: { placeholder: "" },
  number: { placeholder: "" },
  slider: { min: 0, max: 100, step: 1 },
  rating: { max: 5, icon: "star" },
  dropdown: { options: [{ label: "Option 1", value: "option_1" }] },
  checkbox: { options: [{ label: "Option 1", value: "option_1" }] },
  yes_no: { trueLabel: "Yes", falseLabel: "No" },
  emoji_scale: {
    emojis: [
      { emoji: "😫", label: "Terrible", value: "1" },
      { emoji: "😟", label: "Bad", value: "2" },
      { emoji: "😐", label: "Okay", value: "3" },
      { emoji: "😊", label: "Good", value: "4" },
      { emoji: "🤩", label: "Great", value: "5" },
    ],
  },
  date: {},
  time: {},
  datetime: {},
};
